import { useState, useCallback, forwardRef, useImperativeHandle, useEffect, useContext } from "react";
import { createEditor, BaseEditor, Node, Descendant, Editor, Transforms } from "slate";
import { Slate, Editable, withReact, ReactEditor, RenderElementProps, RenderLeafProps } from "slate-react";
import { withHistory } from "slate-history";

import editorStyle from "../../../../reportWriter.module.css";
import { AuthContext } from "../../../../../../../../contexts/AuthContext";
import { apiImageUpload } from "../../../../../../../../sdk";
import { EditorContext } from "../../../../../../../../contexts/EditorContext";
import { useDebouncedCallback } from "use-debounce";

interface CustomElement {
  type: "paragraph" | "code";
  children: CustomText[];
}
interface CustomText {
  text: string;
  bold?: boolean;
}

declare module "slate" {
  interface CustomTypes {
    Editor: BaseEditor & ReactEditor;
    Element: CustomElement;
    Text: CustomText;
  }
}

const CodeElement = (props: RenderElementProps) => {
  return (
    <pre {...props.attributes}>
      <code>{props.children}</code>
    </pre>
  );
};
const DefaultElement = (props: RenderElementProps) => {
  return <p {...props.attributes}>{props.children}</p>;
};
const Leaf = (props: RenderLeafProps) => {
  return (
    <span {...props.attributes} style={{ fontWeight: props.leaf.bold ? "bold" : "normal" }}>
      {props.children}
    </span>
  );
};

export type ListingRef = {
  selectionMark: (mark: string, mark2?: string) => void;
  localValue: string
};

const SlateEditor = forwardRef<ListingRef>((_, ref) => {
  const { token } = useContext(AuthContext);

  const { value, setValue } = useContext(EditorContext);
  const [localValue, setLocalValue] = useState(value);
  const debouncedCallback = useDebouncedCallback((newValue) => {
    setValue(newValue);
  }, 300);

  const [editor] = useState(() => withReact(withHistory(createEditor())));

  const safeSetValue = (newValue: string) => {
    const finalValue = newValue.trim() === "" ? " " : newValue;
    setLocalValue(finalValue);
    debouncedCallback(finalValue);
  };

  useEffect(() => {if(value !== localValue) setLocalValue(value)}, [value]);

  useEffect(() => {
    ReactEditor.focus(editor);
  }, [editor]);

  function toggleSelectionMark(mark: string, mark2?: string) {
    const { selection } = editor;
    const endMark = mark2 ?? mark;

    if (selection) {
      const text = Editor.string(editor, selection);
      const isAlreadyBold = text.startsWith(mark) && text.endsWith(endMark);

      const start = Editor.start(editor, selection);
      const end = Editor.end(editor, selection);
      let endOffset;

      if (!isAlreadyBold) {
        Transforms.insertText(editor, `${mark}${text}${endMark}`, { at: selection });
        endOffset = mark.length + endMark.length;
      } else {
        const trimmedText = text.slice(mark.length, -endMark.length);
        Transforms.insertText(editor, trimmedText, { at: selection });
        endOffset = -mark.length - endMark.length;
      }

      Transforms.setSelection(editor, {
        anchor: {
          path: start.path,
          offset: start.offset,
        },
        focus: {
          path: end.path,
          offset: end.offset + endOffset,
        },
      });
    }
  }

  useImperativeHandle(ref, () => ({
    selectionMark: (mark: string, mark2?: string) => {
      toggleSelectionMark(mark, mark2);
    },
    localValue: localValue,
  }));

  const initialValue: Descendant[] = [
    {
      type: "paragraph",
      children: [{ text: localValue }],
    },
  ];

  const serialize = (nodes: Node[]) => {
    if (Array.isArray(nodes) && nodes.length > 1) return nodes.map((n) => Node.string(n)).join("\n");
    return Node.string(nodes[0]);
  };

  const handleEditorChange = (newValue: Node[]) => {
    clampSelection(editor);
    safeSetValue(serialize(newValue));
  };

  const renderElement = useCallback((props: RenderElementProps) => {
    switch (props.element.type) {
      case "code":
        return <CodeElement {...props} />;
      default:
        return <DefaultElement {...props} />;
    }
  }, []);
  const renderLeaf = useCallback((props: RenderLeafProps) => {
    return <Leaf {...props} />;
  }, []);
  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.ctrlKey) {
      if (event.key === "b") {
        event.preventDefault();
        toggleSelectionMark("**");
      } else if (event.key === "i") {
        event.preventDefault();
        toggleSelectionMark("_");
      } else if (event.key === "X") {
        event.preventDefault();
        toggleSelectionMark("~~");
      } else if (event.key === "j") {
        event.preventDefault();
        toggleSelectionMark("\n```\n");
      } else if (event.key === "p") {
        event.preventDefault();
        toggleSelectionMark("![Alt text](", ")");
      }
    }
  };

  async function checkPasteImage(evt: React.ClipboardEvent) {
    const clipboardItems = evt.clipboardData.items;
    const items = Array.from(clipboardItems).filter(function (item) {
      // Filter the image items only
      return item.type.startsWith("image/");
    });
    if (items.length === 0) {
      return;
    }

    const item = items[0];
    const blob = item.getAsFile();

    if (!blob) return;

    const response_unknown = await apiImageUpload(token, blob);
    const response = JSON.parse(JSON.stringify(response_unknown));
    console.log("Uploaded:", response.data.filePath);

    toggleSelectionMark("![", `](${import.meta.env.VITE_API_URL}/${response.data.filePath})`);
  }

  return (
    <Slate editor={editor} initialValue={initialValue} onChange={handleEditorChange}>
      <Editable
        className={editorStyle.editorText}
        renderElement={renderElement}
        renderLeaf={renderLeaf}
        onKeyDown={handleKeyDown}
        onPaste={checkPasteImage}
      />
    </Slate>
  );
});

function clampSelection(editor: Editor) {
  const { selection } = editor;
  if (!selection) return;

  try {
    // Retrieve the text for anchor/focus
    const [anchorNode] = Editor.node(editor, selection.anchor);
    const [focusNode] = Editor.node(editor, selection.focus);
    const anchorText = Node.string(anchorNode);
    const focusText = Node.string(focusNode);

    // Clamp offsets to the node length
    const newAnchorOffset = Math.min(selection.anchor.offset, anchorText.length);
    const newFocusOffset = Math.min(selection.focus.offset, focusText.length);

    // Update selection safely
    Transforms.setSelection(editor, {
      anchor: { path: selection.anchor.path, offset: newAnchorOffset },
      focus: { path: selection.focus.path, offset: newFocusOffset },
    });
  } catch (error) {
    // If the selection is invalid, select the editor’s end
    const end = Editor.end(editor, []);
    Transforms.select(editor, end);
    console.error("Selection exceeded node boundary. Reset to editor end:", error);
  }
}

export default SlateEditor;
